import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    validateTokenOnLoad, 
    refreshFirebaseToken, 
    isAuthenticated,
    clearAuthData,
    SESSION_EXPIRED_EVENT
} from '../utils/auth';
import { CORE_API_URL } from '../api/api';
import SessionExpiredModal from './SessionExpiredModal';

const HEARTBEAT_INTERVAL = 3 * 60 * 1000; // 3 minutes
const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const PUBLIC_ROUTES = ['/login', '/signup', '/privacy-policy'];

const isPublicRoute = (pathname) => {
    if (PUBLIC_ROUTES.includes(pathname)) return true;
    if (pathname.startsWith('/job-posting/')) return true;
    // Dev-only sidebar playground (route only exists in development builds)
    if (process.env.NODE_ENV === 'development' && pathname === '/nav-preview') return true;
    return false;
};

/**
 * AuthProvider
 *
 * Wraps the app and manages:
 *  - One-time session validation on mount (via /auth/me)
 *  - Periodic Firebase ID token refresh (every 5 min)
 *  - Periodic session heartbeat (every 3 min via /auth/me) — the server
 *    extends the session when its remaining lifetime is short, so active
 *    users are never logged out
 *  - Immediate heartbeat on tab re-focus (visibilitychange)
 *  - Graceful "session expired" modal instead of a hard redirect
 */
const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const refreshIntervalRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    const isInitializedRef = useRef(false);
    const sessionExpiredRef = useRef(false);
    const [sessionExpired, setSessionExpired] = useState(false);

    const markSessionExpired = () => {
        if (sessionExpiredRef.current) return;
        sessionExpiredRef.current = true;
        setSessionExpired(true);
    };

    // ── Heartbeat ──────────────────────────────────────────────────────
    // Pings /auth/me to check session validity. The server extends the
    // session when remaining time < HEARTBEAT_GRACE_MINUTES (10 min),
    // so as long as heartbeats arrive the user stays logged in.
    const performHeartbeat = async () => {
        if (!isAuthenticated() || sessionExpiredRef.current) return;

        try {
            const response = await fetch(`${CORE_API_URL}/auth/me`, {
                method: 'GET',
                credentials: 'include',
            });

            if (!response.ok) {
                clearAuthData();
                markSessionExpired();
                return;
            }

            // A successful /auth/me means the session is still valid. The HR
            // portal no longer gates on user_type, so there is no further check.
        } catch {
            // Network error — don't show modal for transient failures.
            // The next heartbeat (or API 401) will catch a real expiry.
        }
    };

    const startHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }
        heartbeatIntervalRef.current = setInterval(performHeartbeat, HEARTBEAT_INTERVAL);
    };

    // ── Firebase token refresh ─────────────────────────────────────────
    const startTokenRefresh = () => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
        }

        refreshIntervalRef.current = setInterval(async () => {
            if (!isAuthenticated()) {
                clearInterval(refreshIntervalRef.current);
                return;
            }

            const newToken = await refreshFirebaseToken();
            if (!newToken) {
                console.error('Failed to refresh Firebase token');
            }
        }, TOKEN_REFRESH_INTERVAL);
    };

    // ── Cleanup helper ─────────────────────────────────────────────────
    const clearAllIntervals = () => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
    };

    // ── Modal action ───────────────────────────────────────────────────
    const handleLogin = () => {
        const returnUrl = encodeURIComponent(location.pathname);
        navigate(`/login?returnTo=${returnUrl}`);
    };

    // ── Init (runs once on mount) ──────────────────────────────────────
    useEffect(() => {
        if (isInitializedRef.current) return;

        const initializeAuth = async () => {
            if (isPublicRoute(location.pathname)) {
                isInitializedRef.current = true;
                return;
            }

            const isValid = await validateTokenOnLoad();

            if (!isValid) {
                const returnUrl = encodeURIComponent(location.pathname);
                navigate(`/login?returnTo=${returnUrl}`);
                isInitializedRef.current = true;
                return;
            }

            startTokenRefresh();
            startHeartbeat();
            isInitializedRef.current = true;
        };

        initializeAuth();

        return () => clearAllIntervals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Route-change guard ─────────────────────────────────────────────
    useEffect(() => {
        if (!isInitializedRef.current || sessionExpiredRef.current) return;

        if (!isPublicRoute(location.pathname) && !isAuthenticated()) {
            clearAuthData();
            const returnUrl = encodeURIComponent(location.pathname);
            navigate(`/login?returnTo=${returnUrl}`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // ── Session-expired event (fired by the 401 interceptor) ───────────
    useEffect(() => {
        const handle = () => markSessionExpired();
        window.addEventListener(SESSION_EXPIRED_EVENT, handle);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handle);
    }, []);

    // ── Auth state change ──────────────────────────────────────────────
    useEffect(() => {
        const handle = () => {
            if (isAuthenticated()) {
                sessionExpiredRef.current = false;
                setSessionExpired(false);
                startTokenRefresh();
                startHeartbeat();
            } else {
                clearAllIntervals();
            }
        };
        window.addEventListener('authStateChange', handle);
        return () => window.removeEventListener('authStateChange', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Tab visibility (immediate heartbeat on re-focus) ───────────────
    useEffect(() => {
        const handle = () => {
            if (
                document.visibilityState === 'visible' &&
                isAuthenticated() &&
                !sessionExpiredRef.current
            ) {
                performHeartbeat();
            }
        };
        document.addEventListener('visibilitychange', handle);
        return () => document.removeEventListener('visibilitychange', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {children}
            {sessionExpired && !isPublicRoute(location.pathname) && <SessionExpiredModal onLogin={handleLogin} />}
        </>
    );
};

export default AuthProvider;
